<template>
  <div class="lead-submissions">
    <div class="lead-submissions__header">
      <h3 class="lead-submissions__title">
        {{ $t('lead_details.form_submissions') }}
        <span class="lead-submissions__count" v-if="!loading">{{ submissions.length }}</span>
      </h3>
    </div>

    <div class="lead-submissions__content">
      <LoadingSpinner v-if="loading" />

      <div v-else-if="submissions.length === 0" class="lead-submissions__empty">
        <BaseIcon name="inbox" size="48" />
        <h4>{{ $t('lead_details.no_submissions') }}</h4>
        <p>{{ $t('lead_details.no_submissions_description') }}</p>
      </div>

      <div v-else class="lead-submissions__grid">
        <div
          v-for="submission in submissions"
          :key="submission.id"
          class="submission-card"
          @click="openSubmissionDetail(submission)"
        >
          <div class="submission-card__header">
            <div class="submission-card__date">
              {{ formatDate(submission.created_at) }}
            </div>
          </div>

          <div class="submission-card__content">
            <div class="submission-card__title">
              <BaseIcon name="zap" size="16" />
              <span>{{ submission.form?.name || 'Unknown Form' }}</span>
            </div>

            <div class="submission-card__preview" v-if="submission.preview_data">
              <div
                v-for="field in submission.preview_data.slice(0, 2)"
                :key="field.fieldId"
                class="preview-field"
              >
                <span class="preview-field__label">{{ field.label }}:</span>
                <span class="preview-field__value">{{ field.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="lead-submissions__pagination">
        <Btn
          variant="secondary"
          size="sm"
          :disabled="pagination.page <= 1"
          @click="changePage(pagination.page - 1)"
        >
          {{ $t('common.previous') }}
        </Btn>

        <span class="pagination-info">
          {{ $t('common.page_of', { current: pagination.page, total: pagination.totalPages }) }}
        </span>

        <Btn
          variant="secondary"
          size="sm"
          :disabled="pagination.page >= pagination.totalPages"
          @click="changePage(pagination.page + 1)"
        >
          {{ $t('common.next') }}
        </Btn>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, onMounted, computed, getCurrentInstance } from 'vue';
  import { useCrmStore } from '@/store/crmStore';
  import { useUIStore } from '@/store/uiStore';
  import { useFormStore } from '@/store/formStore';
  import { formatDate } from '@/utils/date';

  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import SubmissionDetails from '@/components/crm/submissionDetails.vue';

  export default defineComponent({
    name: 'LeadSubmissions',

    components: {
      LoadingSpinner,
      BaseIcon,
      Btn,
    },

    props: {
      leadId: {
        type: String,
        required: true,
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const crmStore = useCrmStore();
      const uiStore = useUIStore();
      const formStore = useFormStore();

      const loading = computed(() => crmStore.leadSubmissionsLoading);
      const submissions = computed(() => crmStore.leadSubmissions);
      const pagination = computed(() => crmStore.leadSubmissionsPagination);

      const getStatusLabel = (status) => {
        const statusLabels = {
          new: $t('submissions.status.new'),
          reviewed: $t('submissions.status.reviewed'),
          processed: $t('submissions.status.processed'),
          archived: $t('submissions.status.archived'),
        };
        return statusLabels[status] || $t('submissions.status.new');
      };

      const openSubmissionDetail = async (submission) => {
        // Haal de volledige form op als die niet compleet is
        let form = submission.form;

        if (!form || !form.pages) {
          // Als form niet compleet is, haal het op via formStore
          try {
            const formId = submission.form_id || submission.form?.id;
            if (formId) {
              await formStore.fetchFormById(formId);
              form = formStore.currentForm;
            }
          } catch (error) {
            console.warn('Could not load form:', error);
          }
        }

        uiStore.addModal(SubmissionDetails, {
          title: $t('submissions.submission_details'),
          submission: submission,
          form: form || null,
          size: 'xxl',
        });
      };

      const changePage = async (page) => {
        if (page >= 1 && page <= pagination.value.totalPages) {
          await crmStore.fetchLeadSubmissions(props.leadId, {
            page,
            limit: pagination.value.limit,
          });
        }
      };

      const loadSubmissions = async () => {
        try {
          await crmStore.fetchLeadSubmissions(props.leadId, { page: 1, limit: 20 });
        } catch (error) {
          console.error('Failed to load lead submissions:', error);
        }
      };

      onMounted(() => {
        loadSubmissions();
      });

      return {
        loading,
        submissions,
        pagination,
        formatDate,
        getStatusLabel,
        openSubmissionDetail,
        changePage,
      };
    },
  });
</script>

<style scoped lang="scss">
  .lead-submissions {
    &__header {
      margin-bottom: 1.5rem;
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-900);
      margin: 0;
    }

    &__count {
      background: var(--slate-100);
      color: var(--slate-600);
      font-size: 0.875rem;
      font-weight: 500;
      padding: 0.25rem 0.5rem;
      border-radius: 0.375rem;
    }

    &__empty {
      text-align: center;
      padding: 3rem 1rem;
      color: var(--slate-500);

      .icon {
        color: var(--slate-300);
        margin-bottom: 1rem;
      }

      h4 {
        margin: 0 0 0.5rem 0;
        font-size: 1.125rem;
        font-weight: 600;
      }

      p {
        margin: 0;
        font-size: 0.875rem;
      }
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 1rem;

      @include md-down {
        grid-template-columns: 1fr;
      }
    }

    &__pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      margin-top: 2rem;
      padding-top: 1.5rem;
      border-top: 1px solid var(--slate-200);

      .pagination-info {
        font-size: 0.875rem;
        color: var(--slate-600);
      }
    }
  }

  .submission-card {
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--brand-300);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    &__status {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__status-text {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-700);
    }

    &__date {
      font-size: 0.75rem;
      color: var(--slate-500);
      font-weight: 500;
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      color: var(--slate-900);

      .icon {
        color: var(--brand-500);
      }
    }

    &__preview {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__form-info {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--slate-600);

      .icon {
        color: var(--slate-400);
      }
    }

    &__actions {
      .icon {
        color: var(--slate-400);
      }
    }
  }

  .preview-field {
    display: flex;
    gap: 0.5rem;
    font-size: 0.875rem;

    &__label {
      color: var(--slate-600);
      font-weight: 500;
      min-width: 80px;
    }

    &__value {
      color: var(--slate-900);
      flex: 1;
      word-break: break-word;
    }
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &--new {
      background-color: var(--blue-500);
    }

    &--reviewed {
      background-color: var(--amber-500);
    }

    &--processed {
      background-color: var(--teal-500);
    }

    &--archived {
      background-color: var(--slate-400);
    }
  }
</style>
